/**
 * Composer · From Tally / CSV mode.
 *
 * Live bulk-import launcher. Mounts the shared `ImportCustomersDialog`
 * (the same one wired to the customers-list toolbar in Phase B). On a
 * successful commit we navigate to `/sales/customers` — the operator just
 * finished a bulk task, so their next affordance is seeing the newly-
 * landed rows, not returning to a single-customer composer.
 *
 * The composer's Save button is hidden while this mode is active
 * (there's no inline draft to commit; the dialog is the save action).
 */
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Upload, FileSpreadsheet, ArrowRight } from "lucide-react";
import { cn } from "@flint/ui";
import { ImportCustomersDialog } from "../index.ts";

export function ComposerTally() {
  const [importOpen, setImportOpen] = useState(false);
  const [imported, setImported] = useState(false);
  const navigate = useNavigate();

  return (
    <section
      aria-label="Import from Tally or CSV"
      className="rounded-md border border-line bg-paper shadow-card"
    >
      <header className="px-[18px] pt-3.5 pb-2 border-b border-line">
        <h3 className="text-sm font-semibold text-ink m-0 flex items-center gap-2">Bulk import</h3>
        <p className="text-xs text-muted mt-0.5 leading-snug">
          Migrate an existing customer ledger from Tally or any CSV export. Up to 500 rows per file
          — Flint maps columns, GSTN-verifies each row, and flags duplicates before committing.
        </p>
      </header>

      <div className="px-[18px] py-[18px] flex flex-col gap-3.5">
        <div
          className={cn(
            "flex items-start gap-2 px-3 py-2.5 rounded-md",
            "bg-sky-50 border border-sky-100 text-sky-700 text-xs leading-snug",
          )}
        >
          <Upload className="w-3.5 h-3.5 mt-px flex-shrink-0 text-sky-600" aria-hidden />
          <div>
            <strong className="font-semibold">How it works.</strong> Upload a Tally export or CSV.
            Flint maps columns, GSTN-verifies each row, flags duplicates against your existing
            customer list, and lets you review before commit.
          </div>
        </div>

        <div className="rounded-md border border-dashed border-line-2 bg-bg p-6 flex flex-col items-center text-center gap-3">
          <span className="inline-grid place-items-center w-10 h-10 rounded-full bg-paper border border-line text-muted">
            <FileSpreadsheet className="w-5 h-5" strokeWidth={1.8} aria-hidden />
          </span>
          <div>
            <p className="text-sm font-semibold text-ink-2 m-0">Upload customer CSV</p>
            <p className="text-xs text-muted mt-1 leading-snug max-w-[44ch]">
              Pick a file, preview the parsed rows, and commit the valid ones. You'll land on the
              customers list when the import is done.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className={cn(
              "inline-flex items-center gap-1.5 h-9 px-3.5 rounded-sm bg-ink text-paper text-sm font-semibold",
              "hover:bg-ink-2",
            )}
          >
            Import CSV
            <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.4} aria-hidden />
          </button>
        </div>
      </div>

      <ImportCustomersDialog
        open={importOpen}
        onOpenChange={(next) => {
          setImportOpen(next);
          // Redirect only when the dialog closes AFTER a successful
          // import — the user has had a chance to read the summary +
          // skipped-row detail. Closing without importing keeps them on
          // the composer so they can retry.
          if (!next && imported) {
            setImported(false);
            void navigate({ to: "/sales/customers" });
          }
        }}
        onImported={() => {
          setImported(true);
        }}
      />
    </section>
  );
}
